// Main application controller for Wordsheet
// Handles screen switching, auth initialization, and game mode selection

import { initDictionary } from './dictionary.js';
import { initAuth, saveNickname, isDiscordContext } from './firebase-config.js';
import { initDiscord, getDisplayName, isDiscordActivity, getDiscordInstanceId, getDiscordChannelId } from './discord.js';
import { getHeaders } from './api.js';
import { Game } from './game.js';
import { Lobby } from './lobby.js';
import { DiscordLobby } from './discord-lobby.js';
import { MultiplayerGame } from './multiplayer.js';
import { DiscordMultiplayerGame } from './discord-multiplayer.js';
import { initAdminTrigger } from './flags.js';
import { showWheelOfFortune } from './wheel.js';

class App {
    constructor() {
        this.currentScreen = 'menu';
        this.playerId = null;
        this.nickname = null;
        this.game = null;
        this.lobby = null;
        this.multiplayerGame = null;
        this.isDiscord = false;

        this.screens = {
            menu: document.getElementById('menu-screen'),
            lobby: document.getElementById('lobby-screen'),
            game: document.getElementById('game-screen')
        };

        this.init();
    }

    async init() {
        // Initialize dictionary first
        try {
            await initDictionary();
        } catch (error) {
            console.error('Failed to load dictionary:', error);
            // Continue anyway - game will use fallback
        }

        // Check if we're running as a Discord Activity
        this.isDiscord = isDiscordActivity();

        if (this.isDiscord) {
            // Show loading spinner while Discord initializes
            const menuContainer = document.querySelector('.menu-container');
            if (menuContainer) menuContainer.classList.add('loading');
            await this.initDiscordMode();
            if (menuContainer) menuContainer.classList.remove('loading');
        } else {
            await this.initWebMode();
        }

        this.bindEvents();
        initAdminTrigger();

        // Check for join URL parameter
        this.handleJoinUrl();
    }

    async initDiscordMode() {
        console.log('Initializing Discord Activity mode');

        try {
            // Initialize Discord SDK and get user info
            const { user, uid } = await initDiscord();

            // Use Firebase UID as player ID (derived from Discord user ID)
            this.playerId = uid;
            this.nickname = getDisplayName(user);

            console.log(`Discord user: ${this.nickname} (${this.playerId})`);

            // Auto-populate nickname field with Discord username (readonly)
            const nicknameInput = document.getElementById('nickname-input');
            if (nicknameInput) {
                nicknameInput.value = this.nickname;
                nicknameInput.readOnly = true;
            }

            // Check if this Activity instance already has a game (for auto-join)
            const existingGameCode = await this.checkInstanceGame();
            if (existingGameCode) {
                console.log(`Found existing game for this instance: ${existingGameCode}`);
                // Auto-join the existing game
                await this.joinMultiplayerGame(existingGameCode);
                return;
            }

            // Fallback: check if this channel has a recent game (for resume after Activity relaunch)
            const channelGameInfo = await this.checkChannelGame();
            if (channelGameInfo) {
                console.log(`Found channel game for resume: ${channelGameInfo.gameCode}`);
                this.showResumePrompt(channelGameInfo);
                return;
            }
        } catch (error) {
            console.error('Failed to initialize Discord:', error);
            // Fall back to web mode on error
            this.isDiscord = false;
            await this.initWebMode();
        }
    }

    // Check if this Discord Activity instance already has an associated game
    async checkInstanceGame() {
        const instanceId = getDiscordInstanceId();
        if (!instanceId) return null;

        try {
            const response = await fetch(`/.proxy/api/instances/${encodeURIComponent(instanceId)}/game`, {
                headers: getHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                if (data.gameCode) {
                    return data.gameCode;
                }
            }
        } catch (e) {
            console.warn('Failed to check instance game:', e);
        }

        return null;
    }

    // Check if this Discord channel has a recent game (fallback for Activity relaunches)
    async checkChannelGame() {
        const channelId = getDiscordChannelId();
        if (!channelId) return null;

        try {
            const response = await fetch(
                `/.proxy/api/channels/${encodeURIComponent(channelId)}/game`,
                { headers: getHeaders() }
            );
            if (response.ok) {
                const data = await response.json();
                if (data.gameCode) return data;
            }
        } catch (e) {
            console.warn('Failed to check channel game:', e);
        }
        return null;
    }

    // Show a resume prompt when a channel has an existing game
    showResumePrompt(gameInfo) {
        // Format "last played X ago"
        const elapsed = Date.now() - gameInfo.lastPlayedAt;
        const minutes = Math.floor(elapsed / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        let timeAgo;
        if (days > 0) {
            timeAgo = `${days} day${days !== 1 ? 's' : ''} ago`;
        } else if (hours > 0) {
            timeAgo = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        } else if (minutes > 0) {
            timeAgo = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        } else {
            timeAgo = 'just now';
        }

        // Build player rows HTML
        let playersHtml = '';
        if (gameInfo.players && gameInfo.players.length > 0) {
            const rows = gameInfo.players.map(p =>
                `<div class="resume-player-row">` +
                    `<span class="resume-player-name">${p.nickname.replace(/</g, '&lt;')}</span>` +
                    `<span class="resume-player-score">${p.score}</span>` +
                `</div>`
            ).join('');
            playersHtml = `<div class="resume-players">${rows}</div>`;
        }

        // Use the same pattern as the scoreboard overlay (inserted via innerHTML
        // into a wrapper to ensure CSS classes are applied after stylesheet loads)
        const overlay = document.createElement('div');
        overlay.className = 'resume-overlay';
        overlay.innerHTML =
            `<div class="resume-modal">` +
                `<h2 class="resume-title">Game In Progress</h2>` +
                `<p class="resume-subtitle">A game is in progress in this channel</p>` +
                `<div class="resume-code">${gameInfo.gameCode.replace(/</g, '&lt;')}</div>` +
                playersHtml +
                `<p class="resume-last-played">Last played ${timeAgo}</p>` +
                `<div class="resume-actions">` +
                    `<button class="resume-btn primary" data-action="resume">Resume Game</button>` +
                    `<button class="resume-btn secondary" data-action="new">New Game</button>` +
                `</div>` +
            `</div>`;

        overlay.addEventListener('click', (e) => {
            const action = e.target.dataset?.action;
            if (action === 'resume') {
                overlay.remove();
                this.joinMultiplayerGame(gameInfo.gameCode);
            } else if (action === 'new') {
                overlay.remove();
            }
        });

        document.body.appendChild(overlay);
    }

    async initWebMode() {
        console.log('Initializing web mode');

        try {
            const authData = await initAuth();
            this.playerId = authData.playerId;
            this.nickname = authData.nickname;

            // Restore nickname input if we have one saved
            const nicknameInput = document.getElementById('nickname-input');
            if (this.nickname && nicknameInput) {
                nicknameInput.value = this.nickname;
            }
        } catch (error) {
            console.error('Failed to initialize auth:', error);
            this.setMultiplayerEnabled(false);
            this.showMenuError('Multiplayer unavailable (auth failed). Single player is still available.');
            // Continue anyway for single player mode
        }
    }

    bindEvents() {
        // Menu screen events
        document.getElementById('single-player-btn').addEventListener('click', () => {
            this.startSinglePlayer();
        });

        document.getElementById('create-game-btn').addEventListener('click', () => {
            this.createMultiplayerGame();
        });

        document.getElementById('join-game-btn').addEventListener('click', () => {
            const codeInput = document.getElementById('join-code-input');
            const code = codeInput.value.trim().toUpperCase();
            if (code.length === 6) {
                this.joinMultiplayerGame(code);
            } else {
                this.showMenuError('Enter a 6-character game code');
            }
        });

        // Allow Enter key to join game
        document.getElementById('join-code-input').addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                document.getElementById('join-game-btn').click();
            }
        });

        // Nickname input - auto-save on change (web mode only)
        const nicknameInput = document.getElementById('nickname-input');
        if (nicknameInput) {
            nicknameInput.addEventListener('change', (e) => {
                const nickname = e.target.value.trim();
                if (nickname && !this.isDiscord) {
                    this.nickname = nickname;
                    saveNickname(nickname);
                }
            });
        }

        // Lobby screen events
        document.getElementById('start-game-btn').addEventListener('click', () => {
            if (this.lobby) {
                this.lobby.startGame();
            }
        });

        document.getElementById('leave-lobby-btn').addEventListener('click', () => {
            this.leaveLobby();
        });

        document.getElementById('copy-code-btn').addEventListener('click', () => {
            this.copyGameLink();
        });

        // Game screen events
        document.getElementById('leave-game-btn').addEventListener('click', () => {
            this.leaveGame();
        });

        document.getElementById('practice-leave-btn').addEventListener('click', () => {
            this.leavePractice();
        });
    }

    setMultiplayerEnabled(enabled) {
        const createBtn = document.getElementById('create-game-btn');
        const joinBtn = document.getElementById('join-game-btn');
        const joinInput = document.getElementById('join-code-input');

        createBtn.disabled = !enabled;
        joinBtn.disabled = !enabled;
        joinInput.disabled = !enabled;

        if (!enabled) {
            joinInput.placeholder = 'Multiplayer unavailable';
        }
    }

    showScreen(screenName) {
        Object.keys(this.screens).forEach(name => {
            if (name === screenName) {
                this.screens[name].classList.remove('hidden');
            } else {
                this.screens[name].classList.add('hidden');
            }
        });
        this.currentScreen = screenName;
    }

    showMenuError(message) {
        // For now, just alert. Could add a proper error message element later.
        alert(message);
    }

    getNickname() {
        // Discord mode: already have nickname from Discord
        if (this.isDiscord && this.nickname) {
            return this.nickname;
        }

        // Web mode: get from input
        const input = document.getElementById('nickname-input');
        let nickname = input ? input.value.trim() : '';
        if (!nickname) {
            nickname = `Player${Math.floor(Math.random() * 1000)}`;
            if (input) input.value = nickname;
        }
        this.nickname = nickname;
        if (!this.isDiscord) {
            saveNickname(nickname);
        }
        return nickname;
    }

    startSinglePlayer() {
        this.showScreen('game');

        // Hide multiplayer panel, show practice leave button, mark as single player
        document.getElementById('multiplayer-panel').classList.add('hidden');
        document.getElementById('game-code-display').textContent = '';
        document.getElementById('practice-leave-btn').classList.add('visible');
        document.querySelector('.game-container').classList.add('single-player');

        if (this.game) {
            this.game.destroy();
            this.game = null;
        }
        if (this.multiplayerGame) {
            this.multiplayerGame.cleanup();
            this.multiplayerGame = null;
        }

        // Create the single player game
        this.game = new Game();
        window.game = this.game;
    }

    async createMultiplayerGame() {
        console.log('createMultiplayerGame called, playerId:', this.playerId, 'isDiscord:', this.isDiscord);
        if (!this.playerId) {
            this.showMenuError('Multiplayer unavailable (auth failed).');
            return;
        }
        const nickname = this.getNickname();
        console.log('Creating game with nickname:', nickname);

        try {
            // Use DiscordLobby in Discord mode, regular Lobby otherwise
            if (this.isDiscord) {
                this.lobby = new DiscordLobby(this.playerId, nickname);
            } else {
                this.lobby = new Lobby(this.playerId, nickname);
            }
            console.log('Lobby created, calling createGame...');
            const gameCode = await this.lobby.createGame();
            console.log('Game created with code:', gameCode);

            // Update URL with game code (without reload) - only in web mode
            if (!this.isDiscord) {
                const url = new URL(window.location);
                url.searchParams.set('join', gameCode);
                window.history.replaceState({}, '', url);
            }

            this.showLobby(gameCode);
        } catch (error) {
            console.error('Failed to create game:', error);
            this.showMenuError('Failed to create game. Please try again.');
        }
    }

    async joinMultiplayerGame(gameCode) {
        if (!this.playerId) {
            this.showMenuError('Multiplayer unavailable (auth failed).');
            return;
        }
        const nickname = this.getNickname();

        try {
            // Use DiscordLobby in Discord mode, regular Lobby otherwise
            if (this.isDiscord) {
                this.lobby = new DiscordLobby(this.playerId, nickname);
            } else {
                this.lobby = new Lobby(this.playerId, nickname);
            }
            const joinResult = await this.lobby.joinGame(gameCode);

            // Update URL with game code - only in web mode
            if (!this.isDiscord) {
                const url = new URL(window.location);
                url.searchParams.set('join', joinResult.gameCode);
                window.history.replaceState({}, '', url);
            }

            if (joinResult.status === 'active') {
                // Show wheel of fortune for late-joiners with bonus
                if (joinResult.lateJoinBonus) {
                    await showWheelOfFortune(joinResult.lateJoinBonus);
                }
                await this.startMultiplayerGame();
            } else {
                this.showLobby(joinResult.gameCode);
            }
        } catch (error) {
            console.error('Failed to join game:', error);
            if (error.message === 'GAME_NOT_FOUND') {
                this.showMenuError('Game not found. Check the code and try again.');
            } else if (error.message === 'GAME_FULL') {
                this.showMenuError('This game is full (max 5 players).');
            } else if (error.message === 'TILE_BAG_TOO_SMALL') {
                this.showMenuError('Not enough tiles left to join this game.');
            } else if (error.message === 'GAME_ENDED') {
                this.showMenuError('This game has ended.');
            } else {
                this.showMenuError('Failed to join game. Please try again.');
            }
        }
    }

    showLobby(gameCode) {
        this.showScreen('lobby');

        // Update game code display
        document.getElementById('lobby-game-code').textContent = gameCode;

        // Hide copy button in Discord (clipboard doesn't work in iframe)
        const copyBtn = document.getElementById('copy-code-btn');
        if (this.isDiscord) {
            copyBtn.style.display = 'none';
        } else {
            copyBtn.style.display = '';
        }

        // Start listening for player updates
        this.lobby.onPlayersUpdate((players) => {
            this.renderLobbyPlayers(players);
        });

        // Start listening for game start
        this.lobby.onGameStart(() => {
            this.startMultiplayerGame();
        });
    }

    renderLobbyPlayers(players) {
        const list = document.getElementById('lobby-players');
        const countSpan = document.getElementById('player-count');

        list.innerHTML = '';
        countSpan.textContent = `(${players.length}/5)`;

        const isHost = players.length > 0 && players[0].id === this.playerId;

        players.forEach((player, index) => {
            const li = document.createElement('li');
            li.textContent = player.nickname;

            if (index === 0) {
                li.classList.add('host');
            }
            if (player.id === this.playerId) {
                li.classList.add('you');
            }

            list.appendChild(li);
        });

        // Enable/disable start button (host only)
        const startBtn = document.getElementById('start-game-btn');
        startBtn.disabled = !isHost;

        if (isHost) {
            document.getElementById('lobby-status').textContent = 'Ready to start!';
        } else {
            document.getElementById('lobby-status').textContent = 'Waiting for host to start...';
        }
    }

    async startMultiplayerGame() {
        this.showScreen('game');

        // Show multiplayer panel, hide practice leave button, mark as multiplayer
        document.getElementById('multiplayer-panel').classList.remove('hidden');
        document.getElementById('game-code-display').textContent = this.lobby.gameCode;
        document.getElementById('practice-leave-btn').classList.remove('visible');
        document.querySelector('.game-container').classList.remove('single-player');

        // Create multiplayer game instance - use Discord version in Discord mode
        if (this.isDiscord) {
            this.multiplayerGame = new DiscordMultiplayerGame(
                this.lobby.gameCode,
                this.playerId,
                this.nickname
            );
        } else {
            this.multiplayerGame = new MultiplayerGame(
                this.lobby.gameCode,
                this.playerId,
                this.nickname
            );
        }

        await this.multiplayerGame.init();
        window.game = this.multiplayerGame.localGame;
    }

    async leaveLobby() {
        if (this.lobby) {
            await this.lobby.leave();
            this.lobby = null;
        }

        // Clear URL parameter - only in web mode
        if (!this.isDiscord) {
            const url = new URL(window.location);
            url.searchParams.delete('join');
            window.history.replaceState({}, '', url);
        }

        this.showScreen('menu');
    }

    async leaveGame() {
        if (this.multiplayerGame) {
            await this.multiplayerGame.leave();
            this.multiplayerGame = null;
        }

        // Clear URL parameter - only in web mode
        if (!this.isDiscord) {
            const url = new URL(window.location);
            url.searchParams.delete('join');
            window.history.replaceState({}, '', url);
        }

        this.showScreen('menu');
    }

    leavePractice() {
        if (this.game) {
            this.game.destroy();
            this.game = null;
        }
        document.getElementById('practice-leave-btn').classList.remove('visible');
        this.showScreen('menu');
    }

    copyGameLink() {
        const gameCode = this.lobby?.gameCode || '';
        const url = new URL(window.location);
        url.searchParams.set('join', gameCode);

        navigator.clipboard.writeText(url.toString()).then(() => {
            const btn = document.getElementById('copy-code-btn');
            btn.textContent = 'Copied!';
            setTimeout(() => {
                btn.innerHTML = '<span>Copy Link</span>';
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    }

    handleJoinUrl() {
        // Skip URL handling in Discord mode
        if (this.isDiscord) return;

        const params = new URLSearchParams(window.location.search);
        const joinCode = params.get('join');

        if (joinCode && joinCode.length === 6) {
            // Pre-fill the join code
            const joinInput = document.getElementById('join-code-input');
            if (joinInput) {
                joinInput.value = joinCode.toUpperCase();
            }

            // If we have a nickname saved, auto-join
            if (this.nickname) {
                this.joinMultiplayerGame(joinCode.toUpperCase());
            }
        }
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.app = new App();
});

export { App };
